/*
 * <door-dimensions> — drives Sound Lock door seal kit variant selection from
 * width / height inputs.
 *
 * This is the DOOR counterpart to <window-dimensions>. They are deliberately
 * SEPARATE components so the two can evolve independently. Do not merge them.
 *
 * What's different from the window selector:
 * - The window selector snaps width and height to a generic "WxH" grid and
 *   drives two options (dimensions + a secondary bottom-seal option).
 * - This door selector drives ONE combined option ("Size"), whose values look
 *   like:  Jamb Seals: 36" x 84" | Bottom Seal: 24"
 *   It computes the exact target Size from the door's width and height using
 *   the documented Sound Lock sizing rules, then selects that variant.
 *
 * Sizing rules (approved):
 *   WIDTH (in) -> jamb seal width + bottom seal
 *     <= 22"      unsupported (too narrow) -> warn + block
 *     23"-36"     36" jamb seals; bottom seal = smallest even >= width
 *     37"-48"     48" jamb seals; bottom seal = smallest even >= width
 *     >= 49"      double door -> warn + block
 *   HEIGHT (in) -> jamb seal height
 *     <= 59"      unsupported (too short) -> warn + block
 *     60"-84"     84" jamb seals
 *     85"-96"     96" jamb seals
 *     >= 97"      "Over 96" -> auto-select the (Request a Quote) variant
 *
 * The selector only ever touches the "Size" option. Color and
 * "Standard or Industrial" stay on the normal variant picker.
 *
 * Data source: a JSON array embedded in the element, one entry per Size
 * option value: { w, h, b, quote, label }. w/h are the jamb seal width/height
 * (h = 9999 for "Over 96"), b is the bottom seal width, quote flags the
 * Request-a-Quote values, label is the exact option value string to select.
 *
 * Re-render resilience mirrors <window-dimensions>: refs are re-looked-up at
 * the start of every apply and from a MutationObserver, the option is set
 * silently, and a single change event is dispatched on a LIVE checked radio /
 * select so it reaches the currently-mounted <variant-selects>/<product-info>.
 *
 * Verbose instrumentation: filter the console by [dd]. Set
 * window.DD_DEBUG = false to silence.
 */

(function () {
  const dbg = (...args) => {
    if (window.DD_DEBUG === false) return;
    console.log('[dd]', ...args);
  };
  const dbgGroup = (label, fn) => {
    if (window.DD_DEBUG === false) { fn(); return; }
    console.groupCollapsed('[dd] ' + label);
    try { fn(); } finally { console.groupEnd(); }
  };

  if (customElements.get('door-dimensions')) return;

  customElements.define(
    'door-dimensions',
    class DoorDimensions extends HTMLElement {
      constructor() {
        super();
        this._debounceMs = 350;
        this._debounceTimer = null;
        this._instanceId = Math.random().toString(36).slice(2, 8);
      }

      connectedCallback() {
        if (this._connected) return;
        this._connected = true;

        this.optionName = this.dataset.optionName || 'Size';
        this.widthInput = this.querySelector('[data-door-width]');
        this.heightInput = this.querySelector('[data-door-height]');

        // Collect every warning variant, keyed by its data-door-warning value
        // (narrow | short | wide | nomatch). A bare [data-door-warning] with no
        // value is treated as the nomatch fallback so older markup still works.
        this.warningEls = {};
        this.querySelectorAll('[data-door-warning]').forEach((el) => {
          const key = (el.getAttribute('data-door-warning') || 'nomatch').trim() || 'nomatch';
          this.warningEls[key] = el;
        });

        const mapScript = this.querySelector('script[data-door-dimensions-map]');
        try {
          this.map = mapScript ? JSON.parse(mapScript.textContent) : [];
        } catch (e) {
          dbg(`#${this._instanceId} JSON parse FAILED on map script`, e);
          this.map = [];
        }
        if (!Array.isArray(this.map)) this.map = [];

        this._refreshRefs(/* invalidateAll */ true);

        this._stableRoot = this.closest('[data-section]') || document.body;
        if (!this._observer) {
          this._observer = new MutationObserver(() => this._scheduleReattach());
          this._observer.observe(this._stableRoot, { childList: true, subtree: true });
        }

        const onInput = () => {
          clearTimeout(this._debounceTimer);
          this._debounceTimer = setTimeout(() => this._apply(), this._debounceMs);
        };
        if (this.widthInput) this.widthInput.addEventListener('input', onInput);
        if (this.heightInput) this.heightInput.addEventListener('input', onInput);

        // Initial state: if both dimensions are already present (e.g. server
        // pre-filled), resolve now; otherwise hold in "incomplete" so add-to-cart
        // stays blocked until the shopper enters a size.
        this._syncState();

        dbgGroup(`#${this._instanceId} connected`, () => {
          dbg('optionName     =', this.optionName);
          dbg('map entries    =', this.map.length);
          dbg('variantSelects =', this.variantSelects ? this.variantSelects.id || '(no id)' : 'NOT FOUND');
          dbg('sizeField      =', this.sizeField ? 'found' : 'NOT FOUND');
          dbg('dimensionMode  =', this._dimensionMode);
        });
      }

      disconnectedCallback() {
        clearTimeout(this._debounceTimer);
        if (this._observer) {
          this._observer.disconnect();
          this._observer = null;
        }
        dbg(`#${this._instanceId} disconnected`);
      }

      // -----------------------------------------------------------------
      // Reference refresh — keeps a live ref to <variant-selects>, clearing
      // child caches if it was replaced by a section re-render.
      // -----------------------------------------------------------------
      _refreshRefs(invalidateAll) {
        const liveVS = this._findVariantSelects();
        const replaced = liveVS !== this.variantSelects;
        if (invalidateAll || replaced) {
          if (replaced) dbg(`#${this._instanceId} variant-selects replaced — invalidating caches`);
          this.variantSelects = liveVS;
          this._dimensionMode = undefined;
          this._dimensionSelect = undefined;
          this.sizeField = undefined;
        }
        this._hideSizeField();
        return replaced;
      }

      _hideSizeField() {
        const field = this._findSizeField();
        if (!field) {
          dbg(`#${this._instanceId} _hideSizeField — field not found`);
          return;
        }
        this.sizeField = field;
        if (field.getAttribute('data-hidden-by-door-dimensions') !== 'true') {
          field.setAttribute('data-hidden-by-door-dimensions', 'true');
        }
      }

      _scheduleReattach() {
        if (this._scheduledReattach) return;
        this._scheduledReattach = true;
        requestAnimationFrame(() => {
          this._scheduledReattach = false;
          this._refreshRefs(/* invalidateAll */ false);
        });
      }

      _findVariantSelects() {
        const id = this.dataset.variantSelectsId;
        if (id) {
          const byId = document.getElementById(id);
          if (byId) return byId;
        }
        const container = this.closest('[data-section]') || document;
        return (
          container.querySelector('variant-selects') ||
          document.querySelector('variant-selects')
        );
      }

      _findSizeField() {
        if (!this.variantSelects) return null;
        const optName = this.optionName.trim();
        const optNameLc = optName.toLowerCase();

        const sel = this.variantSelects.querySelector(`select[name="options[${optName}]" i]`);
        if (sel) {
          this._dimensionMode = 'select';
          this._dimensionSelect = sel;
          return sel.closest('.product-form__input') || sel.parentElement;
        }

        const radios = this.variantSelects.querySelectorAll('input[type="radio"][name]');
        for (const r of radios) {
          const n = (r.name || '').trim();
          const dash = n.lastIndexOf('-');
          const stem = dash > 0 ? n.slice(0, dash) : n;
          if (stem.toLowerCase() === optNameLc) {
            this._dimensionMode = 'inputs';
            return (
              r.closest('fieldset.product-form__input') ||
              r.closest('.product-form__input') ||
              r.closest('fieldset')
            );
          }
        }

        const fields = this.variantSelects.querySelectorAll('.product-form__input');
        for (const fs of fields) {
          const lab = fs.querySelector('legend, label');
          if (!lab) continue;
          const txt = lab.textContent.trim().toLowerCase();
          if (txt.startsWith(optNameLc)) {
            this._dimensionMode = this._dimensionMode || 'inputs';
            return fs;
          }
        }
        return null;
      }

      // -----------------------------------------------------------------
      // Sizing math
      // -----------------------------------------------------------------

      // Smallest even integer >= n.
      _nextEven(n) {
        const c = Math.ceil(n);
        return c % 2 === 0 ? c : c + 1;
      }

      // Returns { status, target } where status is:
      //   'ok'    — a matching Size value exists (target = label to select)
      //   'quote' — matched a Request-a-Quote value (target = label)
      //   'oversize' / 'narrow' / 'short' / 'nomatch' — no usable variant
      _resolve(w, h) {
        if (!(w > 0) || !(h > 0)) return { status: 'incomplete' };

        // Width -> jamb width
        let jambW;
        if (w <= 22) return { status: 'narrow' };
        if (w <= 36) jambW = 36;
        else if (w <= 48) jambW = 48;
        else return { status: 'wide' }; // double door

        const bottom = this._nextEven(w);

        // Height -> jamb height (or quote)
        if (h < 60) return { status: 'short' };
        let quote = false;
        let jambH = null;
        if (h <= 84) jambH = 84;
        else if (h <= 96) jambH = 96;
        else quote = true; // h >= 97

        const match = this.map.find((e) =>
          e.w === jambW &&
          e.b === bottom &&
          (quote ? e.quote === true : (e.quote !== true && e.h === jambH))
        );

        if (!match) {
          dbg(`_resolve(${w},${h}) no map entry for jambW=${jambW} jambH=${jambH} bottom=${bottom} quote=${quote}`);
          return { status: 'nomatch' };
        }
        return { status: quote ? 'quote' : 'ok', target: match.label };
      }

      // -----------------------------------------------------------------
      // Apply
      // -----------------------------------------------------------------
      _apply() {
        this._refreshRefs(/* invalidateAll */ false);

        const w = parseFloat(this.widthInput && this.widthInput.value);
        const h = parseFloat(this.heightInput && this.heightInput.value);

        const res = this._resolve(w, h);
        dbgGroup(`#${this._instanceId} _apply w=${w} h=${h} -> ${res.status}`, () => {});

        if (res.status === 'incomplete') {
          this._setState('incomplete');
          return;
        }
        if (res.status !== 'ok' && res.status !== 'quote') {
          // narrow / wide / short / nomatch — unsupported size. Pass the reason
          // through so the matching message variant is shown.
          this._setState('out-of-range', res.status);
          return;
        }

        // Matched (normal or quote). Select the Size value, allow checkout.
        this._setState('ok');
        const changed = this._selectOptionValue(res.target, /* dispatch */ false);
        if (!changed) {
          dbg('size already selected — nothing to dispatch');
          return;
        }

        const liveField = this._findSizeField();
        const dispatchTarget = this._dimensionMode === 'select' && this._dimensionSelect
          ? this._dimensionSelect
          : (liveField && liveField.querySelector('input[type="radio"]:checked'));

        if (!dispatchTarget) {
          dbg('NO LIVE dispatch target found — variant will not update (stale sizeField?)');
          return;
        }
        if (!document.contains(dispatchTarget)) {
          dbg('dispatchTarget is detached — refusing to dispatch');
          return;
        }
        dbg(`dispatching change on live ${dispatchTarget.tagName.toLowerCase()} (value="${dispatchTarget.value}")`);
        dispatchTarget.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // -----------------------------------------------------------------
      // State + checkout gating
      //   incomplete   -> block checkout, no message (need a size)
      //   out-of-range -> block checkout, show the message for `reason`
      //   ok           -> allow checkout, clear warnings
      //
      // `reason` is the _resolve status (narrow | short | wide | nomatch) and
      // selects which warning variant to show. Anything unrecognized falls back
      // to the nomatch message.
      // -----------------------------------------------------------------
      _setState(state, reason) {
        this.setAttribute('data-door-state', state);
        const key = state === 'out-of-range' ? this._warningKeyFor(reason) : null;
        this._showWarning(key);
        const form = this._findProductForm();
        if (form) {
          form.classList.toggle('product-form--door-out-of-range', state === 'out-of-range');
          form.classList.toggle('product-form--door-incomplete', state === 'incomplete');
        }
      }

      // Maps a _resolve status to a warning variant key. narrow/short/wide have
      // dedicated messages; everything else uses the nomatch fallback.
      _warningKeyFor(reason) {
        if (reason === 'narrow' || reason === 'short' || reason === 'wide') {
          return this.warningEls[reason] ? reason : 'nomatch';
        }
        return 'nomatch';
      }

      // Shows only the warning whose key matches `key`, hiding the rest. Pass
      // null/undefined to hide all warnings.
      _showWarning(key) {
        if (!this.warningEls) return;
        Object.keys(this.warningEls).forEach((k) => {
          const el = this.warningEls[k];
          if (!el) return;
          if (k === key) {
            el.removeAttribute('hidden');
            el.classList.add('show');
          } else {
            el.setAttribute('hidden', '');
            el.classList.remove('show');
          }
        });
      }

      _syncState() {
        const w = parseFloat(this.widthInput && this.widthInput.value);
        const h = parseFloat(this.heightInput && this.heightInput.value);
        if (w > 0 && h > 0) {
          this._apply();
        } else {
          this._setState('incomplete');
        }
      }

      _findProductForm() {
        if (this._productForm && document.contains(this._productForm)) {
          return this._productForm;
        }
        const root = this._stableRoot || document;
        this._productForm = root.querySelector('.product-form');
        return this._productForm;
      }

      // Returns true if the option's value actually changed.
      _selectOptionValue(label, dispatch = true) {
        if (!this.variantSelects) {
          dbg('_selectOptionValue: no variantSelects');
          return false;
        }
        const target = String(label).trim();

        if (this._dimensionMode === 'select' && this._dimensionSelect) {
          const sel = this._dimensionSelect;
          const opt = Array.from(sel.options).find((o) => o.text.trim() === target || (o.value || '').trim() === target);
          if (!opt) {
            dbg(`_selectOptionValue [select]: NO OPTION matches "${target}"`);
            return false;
          }
          if (sel.value === opt.value) return false;
          sel.value = opt.value;
          if (dispatch) sel.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }

        const root = this.sizeField || this.variantSelects;
        const inputs = root.querySelectorAll('input[type="radio"]');
        const allValues = Array.from(inputs).map((r) => (r.value || '').trim());
        for (const input of inputs) {
          const val = (input.value || '').trim();
          let labelText = '';
          if (input.id) {
            const l = root.querySelector(`label[for="${input.id}"]`);
            if (l) labelText = l.textContent.trim();
          }
          if (val === target || labelText === target) {
            if (input.checked) return false;
            input.checked = true;
            if (dispatch) input.dispatchEvent(new Event('change', { bubbles: true }));
            return true;
          }
        }
        dbg(`_selectOptionValue [radio]: NO RADIO matches "${target}". Available: [${allValues.join(' | ')}]`);
        return false;
      }
    }
  );
})();
